import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useRecoilValue } from 'recoil';
import DashBoardHeader from '@components/salesDashBoard/DashBoardHeader';
import storeInfo from '../../recoil/storeInfo/atom';
import { instance } from '@apis/index';
import { isDemoSession } from '@services/demo/demoAccount';

type DeviceRow = {
  cat_id?: string;
  device_alias?: string;
  txn_count?: number;
  net_amount?: number;
};

type HourRow = {
  hour_of_day?: number;
  day_of_week?: number;
  txn_count?: number;
  net_amount?: number;
};

type TrendRow = DeviceRow & {
  current_amount?: number;
  previous_amount?: number;
  delta?: number;
  current_count?: number;
  previous_count?: number;
};

type TrendSpan = {
  currentLabel?: string;
  previousLabel?: string;
  rising?: TrendRow[];
  falling?: TrendRow[];
};

type YearMonthRow = {
  month?: number;
  net_amount?: number;
  previous_amount?: number;
  delta?: number | null;
};

type YearBody = {
  yearTotal?: number;
  previousYearTotal?: number;
  monthCount?: number;
  previousMonthCount?: number;
  averageBase?: number;
  previousAverageBase?: number;
  openMonth?: number;
  projectedMonth?: number;
  months?: YearMonthRow[];
};

type PeakBody = {
  cached?: boolean;
  elapsedMs?: number;
  computedDays?: number;
  devices?: DeviceRow[];
  hours?: HourRow[];
  hoursByWeekday?: HourRow[];
  week?: TrendSpan;
  monthCompare?: TrendSpan;
};

const WEEKDAYS = [
  { id: 0, label: '전체' },
  { id: 2, label: '월' },
  { id: 3, label: '화' },
  { id: 4, label: '수' },
  { id: 5, label: '목' },
  { id: 6, label: '금' },
  { id: 7, label: '토' },
  { id: 1, label: '일' },
];

const money = (value: number) => `${Number(value || 0).toLocaleString('ko-KR')}`;

const deviceLabel = (alias: string | undefined, catId: string | undefined, storeLabel: string) => {
  const raw = (alias || catId || '').trim();
  const storeName = storeLabel.replace(/^\d+\.\s*/, '').trim();
  if (!storeName || !raw.startsWith(storeName)) return raw;
  return raw.slice(storeName.length).replace(/^[\s._·-]+/, '') || raw;
};

const SalesAnalysis = () => {
  const store = useRecoilValue(storeInfo);
  const [month, setMonth] = useState(() => format(new Date(), 'yyyy-MM'));
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');
  const [body, setBody] = useState<PeakBody | null>(null);
  const [mode, setMode] = useState<'year' | 'device' | 'hour' | ''>('');
  const [kind, setKind] = useState<'device' | 'hour' | 'up' | 'down' | ''>('');
  const [weekday, setWeekday] = useState(0);
  const [span, setSpan] = useState<'week' | 'month'>('week');
  const [showCount, setShowCount] = useState(false);
  const [showAmounts, setShowAmounts] = useState(false);
  const [deviceOrder, setDeviceOrder] = useState<'desc' | 'asc'>('desc');
  const [yearBody, setYearBody] = useState<YearBody | null>(null);
  const [yearLoading, setYearLoading] = useState(false);

  const load = async (nextKind: 'device' | 'hour' | 'up' | 'down') => {
    if (!store.currentStoreId) return;
    setKind(nextKind);
    setLoading(nextKind);
    setError('');
    try {
      const { data } = await instance.get<PeakBody>('/sales/peak', {
        params: {
          storeId: store.currentStoreId,
          month,
          kind: nextKind === 'device' || nextKind === 'hour' ? nextKind : 'trend',
        },
      });
      setBody(data);
    } catch {
      setBody(null);
      setError('집계를 불러오지 못했습니다.');
    } finally {
      setLoading('');
    }
  };

  const year = month.slice(0, 4);

  const [pickedMonth, setPickedMonth] = useState(0);

  useEffect(() => {
    if (isDemoSession()) setMode('year');
  }, []);

  useEffect(() => {
    if (mode !== 'year' || !store.currentStoreId) return undefined;
    let cancel = false;
    setYearLoading(true);
    instance
      .get<YearBody>('/sales/peak', {
        params: { storeId: store.currentStoreId, month: `${year}-01`, kind: 'year' },
      })
      .then(({ data }) => {
        if (!cancel) setYearBody(data);
      })
      .catch(() => {
        if (!cancel) setYearBody(null);
      })
      .finally(() => {
        if (!cancel) setYearLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [mode, store.currentStoreId, year]);

  const yearMonths = Array.from({ length: 12 }, (_, index) => {
    const row = yearBody?.months?.find((item) => Number(item.month) === index + 1);
    return {
      month: index + 1,
      amount: Number(row?.net_amount || 0),
      previous: Number(row?.previous_amount || 0),
      delta: row?.delta == null ? null : Number(row.delta),
    };
  });
  const maxYear = Math.max(1, ...yearMonths.flatMap((row) => [row.amount, row.previous]));
  const monthCount = Number(yearBody?.monthCount || 0);
  const previousMonthCount = Number(yearBody?.previousMonthCount || 0);
  const thisAverage =
    monthCount > 0 ? Math.round(Number(yearBody?.averageBase || 0) / monthCount) : 0;
  const lastAverage =
    previousMonthCount > 0
      ? Math.round(Number(yearBody?.previousAverageBase || 0) / previousMonthCount)
      : 0;
  const averageDelta = thisAverage - lastAverage;
  const projectedMonth = Number(yearBody?.projectedMonth || 0);
  const thisYearSum = Number(yearBody?.averageBase || 0);
  const lastYearSum = Number(yearBody?.previousAverageBase || 0);
  const yearSumDelta = thisYearSum - lastYearSum;
  const shownMonth =
    yearMonths.find((row) => row.month === pickedMonth) ||
    [...yearMonths].reverse().find((row) => row.amount > 0 || row.previous > 0);

  const hourRows =
    (weekday === 0
      ? body?.hours
      : body?.hoursByWeekday?.filter((row) => Number(row.day_of_week) === weekday)) || [];
  const hours = Array.from({ length: 24 }, (_, hour) => {
    const row = hourRows.find((item) => Number(item.hour_of_day) === hour);
    return {
      hour,
      amount: Number(row?.net_amount || 0),
      count: Number(row?.txn_count || 0),
    };
  });
  const maxHour = Math.max(1, ...hours.map((row) => row.amount));
  const peak = hours.reduce((best, row) => (row.amount > best.amount ? row : best), hours[0]);

  return (
    <div id="sales_dashboard_container">
      <DashBoardHeader />
      <div className="dashboard_right_container peak_analysis" data-tour="analysis">
        <p className="peak_analysis_lead">
          {store.currentStore || '지점'} · 어제까지를 날짜별로 저장합니다. 오늘은 집계하지 않습니다.
        </p>
        <div className="peak_analysis_actions">
          <button
            type="button"
            className={mode === 'year' ? 'on' : ''}
            onClick={() => setMode('year')}
            disabled={!store.currentStoreId}
          >
            연 매출 추이
          </button>
          <button
            type="button"
            className={mode === 'device' ? 'on' : ''}
            onClick={() => {
              setMode('device');
              load('device');
            }}
            disabled={!store.currentStoreId}
          >
            단말기 매출
          </button>
          <button
            type="button"
            className={mode === 'hour' ? 'on' : ''}
            onClick={() => {
              setMode('hour');
              load('hour');
            }}
            disabled={!store.currentStoreId || !!loading}
          >
            사용시간 분석
          </button>
        </div>
        <label className="peak_month">
          <select
            aria-label="연도"
            value={month.slice(0, 4)}
            onChange={(event) => setMonth(`${event.target.value}-${month.slice(5, 7)}`)}
          >
            {Array.from({ length: 3 }, (_, index) => new Date().getFullYear() - 2 + index).map(
              (item) => (
                <option key={item} value={item}>
                  {item}년
                </option>
              ),
            )}
          </select>
          {(mode === 'device' || mode === 'hour') && (
            <select
              aria-label="월"
              value={month.slice(5, 7)}
              onChange={(event) => setMonth(`${month.slice(0, 4)}-${event.target.value}`)}
            >
              {Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0')).map(
                (value) => (
                  <option key={value} value={value}>
                    {Number(value)}월
                  </option>
                ),
              )}
            </select>
          )}
        </label>
        {mode === 'device' && (
          <div className="peak_analysis_actions">
            <button
              type="button"
              className={kind === 'device' ? 'on' : ''}
              onClick={() => load('device')}
              disabled={!!loading}
            >
              매출 순위
            </button>
            <button
              type="button"
              className={kind === 'up' ? 'on' : ''}
              onClick={() => load('up')}
              disabled={!!loading}
            >
              매출 증가
            </button>
            <button
              type="button"
              className={kind === 'down' ? 'on' : ''}
              onClick={() => load('down')}
              disabled={!!loading}
            >
              매출 감소
            </button>
          </div>
        )}
        {mode === 'year' && yearLoading && (
          <div className="peak_loading" role="status">
            <span className="peak_loading_bar" />
            <p>일매출을 월별로 합산 중입니다.</p>
          </div>
        )}
        {mode === 'year' && yearBody && !yearLoading && (
          <>
            <div className="peak_average">
              <span className="peak_average_label">평균 매출</span>
              <span className="peak_average_side">
                <i>{String(year).slice(2)}년</i>
                {money(thisAverage)}
              </span>
              <strong className={averageDelta > 0 ? 'up' : averageDelta < 0 ? 'down' : ''}>
                {averageDelta > 0 ? '+' : ''}
                {money(averageDelta)}원
              </strong>
              <span className="peak_average_side last">
                <i>{String(Number(year) - 1).slice(2)}년</i>
                {money(lastAverage)}
              </span>
            </div>
            {projectedMonth > 0 && (
              <p className="peak_peak_note">(이번달 예상 매출 {money(projectedMonth)}원 적용)</p>
            )}
            <p className="trend_legend">
              <i className="prev" /> {Number(year) - 1}년
              <i className="now up" /> {year}년
            </p>
            <div className="peak_chart year_chart" role="img" aria-label="연간 월별 매출">
              {yearMonths.map((row) => (
                <button
                  key={row.month}
                  type="button"
                  className={shownMonth?.month === row.month ? 'col on' : 'col'}
                  onClick={() => setPickedMonth(row.month)}
                >
                  <span className="col_bar year_pair">
                    <i
                      className="last"
                      style={{
                        height: `${row.previous > 0 ? Math.max(4, (row.previous / maxYear) * 100) : 0}%`,
                      }}
                    />
                    <i
                      className="now"
                      style={{
                        height: `${row.amount > 0 ? Math.max(4, (row.amount / maxYear) * 100) : 0}%`,
                      }}
                    />
                  </span>
                  <span className="col_hour">{row.month}</span>
                </button>
              ))}
            </div>
            <p className="year_month_head">
              <span />
              <span>{Number(year) - 1}년</span>
              <span>{year}년</span>
              <span>차이</span>
            </p>
            <ol className="year_month_list">
              {yearMonths
                .filter((row) => row.amount > 0 || row.previous > 0)
                .map((row) => {
                  const delta = row.amount - row.previous;
                  return (
                    <li key={row.month}>
                      <span>{row.month}월</span>
                      <span>{money(row.previous)}</span>
                      <span>{money(row.amount)}</span>
                      <span className={delta > 0 ? 'delta up' : delta < 0 ? 'delta down' : 'delta'}>
                        {delta > 0 ? '+' : ''}
                        {money(delta)}
                      </span>
                    </li>
                  );
                })}
            </ol>
            <div className="peak_average year_sum">
              <span className="peak_average_label">합계</span>
              <span className="peak_average_side">
                <i>{String(year).slice(2)}년</i>
                {money(thisYearSum)}
              </span>
              <strong className={yearSumDelta > 0 ? 'up' : yearSumDelta < 0 ? 'down' : ''}>
                {yearSumDelta > 0 ? '+' : ''}
                {money(yearSumDelta)}원
              </strong>
              <span className="peak_average_side last">
                <i>{String(Number(year) - 1).slice(2)}년</i>
                {money(lastYearSum)}
              </span>
            </div>
          </>
        )}
        {mode !== 'year' && loading && (
          <div className="peak_loading" role="status">
            <span className="peak_loading_bar" />
            <p>이 지점 집계 중입니다. 저장된 날짜는 다시 조회하지 않습니다.</p>
          </div>
        )}
        {error && <p>{error}</p>}
        {mode !== 'year' && body && !loading && (
          <div className="peak_analysis_meta">
            <span className="peak_meta_actions">
              <button
                type="button"
                className={showCount ? 'on' : ''}
                onClick={() => setShowCount((value) => !value)}
              >
                결제 건수 표기
              </button>
              {kind === 'device' && (
                <>
                  <button
                    type="button"
                    className={deviceOrder === 'desc' ? 'on' : ''}
                    onClick={() => setDeviceOrder('desc')}
                  >
                    많은 순
                  </button>
                  <button
                    type="button"
                    className={deviceOrder === 'asc' ? 'on' : ''}
                    onClick={() => setDeviceOrder('asc')}
                  >
                    적은 순
                  </button>
                </>
              )}
            </span>
          </div>
        )}
        {mode === 'hour' && kind === 'hour' && body?.hours && (
          <>
            <div className="peak_weekday" role="group" aria-label="요일">
              {WEEKDAYS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={weekday === item.id ? 'on' : ''}
                  onClick={() => setWeekday(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <p className="peak_peak">
              피크 {peak.hour}시 · {money(peak.amount)}
              {showCount ? ` · ${peak.count.toLocaleString('ko-KR')}건` : ''}
            </p>
            <div className="peak_chart" role="img" aria-label="시간대별 매출">
              {hours.map((row) => (
                <div
                  key={row.hour}
                  className={row.hour === peak.hour && row.amount > 0 ? 'col peak' : 'col'}
                >
                  <span className="col_bar">
                    <i
                      style={{
                        height: `${Math.max(row.amount > 0 ? 4 : 0, (row.amount / maxHour) * 100)}%`,
                      }}
                    />
                  </span>
                  <span className="col_hour">{row.hour}</span>
                </div>
              ))}
            </div>
          </>
        )}
        {mode === 'device' && (kind === 'up' || kind === 'down') && body && (
          <>
            <div className="peak_weekday" role="group" aria-label="비교 기간">
              <button
                type="button"
                className={span === 'week' ? 'on' : ''}
                onClick={() => setSpan('week')}
              >
                주간
              </button>
              <button
                type="button"
                className={span === 'month' ? 'on' : ''}
                onClick={() => setSpan('month')}
              >
                월간
              </button>
              <button
                type="button"
                className={showAmounts ? 'on' : ''}
                onClick={() => setShowAmounts((value) => !value)}
              >
                각각 금액
              </button>
            </div>
            <TrendList
              span={span === 'week' ? body.week : body.monthCompare}
              direction={kind}
              storeLabel={store.currentStore || ''}
              showAmounts={showAmounts}
            />
          </>
        )}
        {mode === 'device' && kind === 'device' && body?.devices && (
          <ol className="peak_device_list">
            {[...body.devices]
              .sort((left, right) =>
                deviceOrder === 'desc'
                  ? Number(right.net_amount || 0) - Number(left.net_amount || 0)
                  : Number(left.net_amount || 0) - Number(right.net_amount || 0),
              )
              .slice(0, 50)
              .map((row, index) => (
                <li key={row.cat_id}>
                  <span className="rank">{index + 1}</span>
                  <span>{deviceLabel(row.device_alias, row.cat_id, store.currentStore || '')}</span>
                  <span className="device_figures">
                    <span className="device_amount">{money(Number(row.net_amount || 0))}</span>
                    {showCount && (
                      <span className="device_count">
                        {Number(row.txn_count || 0).toLocaleString('ko-KR')}
                      </span>
                    )}
                  </span>
                </li>
              ))}
          </ol>
        )}
      </div>
    </div>
  );
};

const TrendList = ({
  span,
  direction,
  storeLabel,
  showAmounts,
}: {
  span?: TrendSpan;
  direction: 'up' | 'down';
  storeLabel: string;
  showAmounts: boolean;
}) => {
  const rows = (direction === 'up' ? span?.rising : span?.falling) || [];
  const maxAmount = Math.max(
    1,
    ...rows.map((row) =>
      Math.max(Number(row.current_amount || 0), Number(row.previous_amount || 0)),
    ),
  );
  return (
    <>
      <p className="peak_peak">
        이번 {span?.currentLabel} · 지난 {span?.previousLabel}
      </p>
      <p className="trend_legend">
        <i className="prev" /> 지난 기간
        <i className={direction === 'up' ? 'now up' : 'now down'} /> 이번 기간
      </p>
      <ol className="trend_list">
        {rows.map((row, index) => {
          const delta = Number(row.delta || 0);
          const current = Number(row.current_amount || 0);
          const previous = Number(row.previous_amount || 0);
          return (
            <li key={row.cat_id}>
              <div className="trend_head">
                <span className="rank">{index + 1}</span>
                <span className="trend_name">
                  {deviceLabel(row.device_alias, row.cat_id, storeLabel)}
                </span>
                <span className={delta > 0 ? 'delta up' : 'delta down'}>
                  {delta > 0 ? '+' : ''}
                  {money(delta)}
                </span>
              </div>
              {showAmounts && (
                <p className="trend_amounts">
                  <span>이번 {money(current)}</span>
                  <span>지난 {money(previous)}</span>
                </p>
              )}
              <div className="trend_track" aria-hidden="true">
                <i className="prev" style={{ width: `${(previous / maxAmount) * 100}%` }} />
              </div>
              <div className="trend_track" aria-hidden="true">
                <i
                  className={delta > 0 ? 'now up' : 'now down'}
                  style={{ width: `${(current / maxAmount) * 100}%` }}
                />
              </div>
            </li>
          );
        })}
      </ol>
      {rows.length === 0 && <p>비교할 증감이 없습니다.</p>}
    </>
  );
};

export default SalesAnalysis;
